/**
 * SDK-owned auth state — the source of truth for "who is signed in".
 *
 * Part of the auth-cutover: the entire auth provider stack relocates from
 * the frontend into `@morfi/backend-sdk-v2` so React Native and web share
 * one implementation (see the cutover plan §Target end-state).
 *
 * This module is the STATE container — a module-level store (pinned on
 * `globalThis`, HMR-safe, mirroring the `jwt-store` pattern). The login
 * flow writes the resolved user via `setSdkAuthState`; UI reads it
 * reactively via `useMorfiAuth()`.
 *
 * Platform-neutral: pure data + a subscribe list. No web/RN APIs — safe
 * in `src/` core per `.claude/rules/platform-agnostic.md`.
 */

/** A linked OAuth provider (X / Google / Apple) on a user. */
export interface SdkConnectedProvider {
  readonly provider: string;
  readonly username?: string;
  readonly displayName?: string;
}

/**
 * The signed-in user, as the SDK exposes it to app code. Field set
 * mirrors the backend profile contract; app-specific display derivations
 * stay in app code.
 */
export interface MorfiUser {
  readonly id: string;
  readonly email: string;
  readonly phoneNumber: string | null;
  readonly displayName: string | null;
  readonly handle: string | null;
  readonly avatarUrl: string | null;
  readonly avatarNsfw: boolean | null;
  readonly avatarPreferences: { readonly rasterise: boolean } | null;
  readonly heroUrl: string | null;
  readonly coverNsfw: boolean | null;
  readonly bio: string | null;
  readonly country: string | null;
  readonly locationSource: string | null;
  readonly createdAt: string;
  readonly onboardingCompleted: boolean;
  readonly connectedProviders: readonly SdkConnectedProvider[];
  readonly turnkeySubOrgId: string | null;
}

/** `loading` until the first session-restore settles; then `authed`/`anon`. */
export type SdkAuthStatus = 'loading' | 'authed' | 'anon';

export interface SdkAuthState {
  readonly status: SdkAuthStatus;
  /** The signed-in user, or `null` when `status !== 'authed'`. */
  readonly user: MorfiUser | null;
}

const LOADING: SdkAuthState = Object.freeze({ status: 'loading', user: null });
const ANON: SdkAuthState = Object.freeze({ status: 'anon', user: null });

export interface SdkAuthStore {
  /** Synchronous read of the current state. */
  current(): SdkAuthState;
  /** Replace the state and notify subscribers (no-op if unchanged). */
  set(next: SdkAuthState): void;
  /** Convenience — `set({ status: user ? 'authed' : 'anon', user })`. */
  setUser(user: MorfiUser | null): void;
  /** Subscribe to changes; returns an unsubscribe fn. */
  subscribe(listener: () => void): () => void;
}

const PIN_KEY = '__morfi_auth_store__' as const;

type GlobalWithStore = typeof globalThis & { [PIN_KEY]?: SdkAuthStore };

function makeStore(): SdkAuthStore {
  let state: SdkAuthState = LOADING;
  const listeners = new Set<() => void>();
  const api: SdkAuthStore = {
    current: () => state,
    set: (next) => {
      if (next.status === state.status && next.user === state.user) return;
      state = next;
      for (const listener of listeners) listener();
    },
    setUser: (user) => {
      api.set(user !== null ? { status: 'authed', user } : ANON);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
  };
  return api;
}

/**
 * Singleton accessor. Pinned on `globalThis` via `??=` so a dev-server
 * hot-reload (or StrictMode double-invoke) reuses the same store and its
 * subscriber list — same pattern as `getJwtStore()`.
 */
export function getSdkAuthStore(): SdkAuthStore {
  const g = globalThis as GlobalWithStore;
  g[PIN_KEY] ??= makeStore();
  return g[PIN_KEY];
}

/** Test-only reset. Production code must not call this. */
export function __resetSdkAuthStoreForTests(): void {
  delete (globalThis as GlobalWithStore)[PIN_KEY];
}
